import { IResource } from "@gettactic/api";
import { useEffect, useState } from "react";
import { MapCanvasValue } from "../components/map/contexts/MapCanvasContext/MapCanvasContext.types";
import { convertSvgToMapCanvasValue } from "../utils/svgImport";

interface UseSvgImportResult {
  importedData: MapCanvasValue | undefined;
  isImporting: boolean;
  importError: string | null;
}

/**
 * Fetches a legacy SVG map from `floor.map_url` and converts it into MapCanvasValue.
 * Only runs when `floor` is provided and has a `.svg` map_url.
 */
export const useSvgImport = (
  floor: IResource | undefined
): UseSvgImportResult => {
  const [importedData, setImportedData] = useState<MapCanvasValue | undefined>(
    undefined
  );
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const mapUrl = floor?.map_url;

  useEffect(() => {
    if (!mapUrl || !mapUrl.endsWith(".svg")) {
      setImportedData(undefined);
      setIsImporting(false);
      setImportError(null);
      return;
    }

    let cancelled = false;
    setIsImporting(true);
    setImportError(null);

    const importSvg = async () => {
      try {
        const response = await fetch(mapUrl);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch SVG: ${response.status} ${response.statusText}`
          );
        }

        const svgText = await response.text();
        if (cancelled) return;

        const result = convertSvgToMapCanvasValue(svgText);
        if (cancelled) return;

        setImportedData(result);
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : "Unknown error importing SVG";
        setImportError(message);
        // eslint-disable-next-line no-console
        console.warn("[SVG Import] Error:", message);
      } finally {
        if (!cancelled) {
          setIsImporting(false);
        }
      }
    };

    importSvg();

    return () => {
      cancelled = true;
    };
  }, [mapUrl]);

  return { importedData, isImporting, importError };
};
